/**
 * Fetch content from brooklyn
 * @module services/content
 */
import request from 'services/request'
import { stringify as qsStringifyQuery } from 'qs'
import { fromJS, Map, Set, List, OrderedSet } from 'immutable'
import { map as mapPromise } from 'bluebird'
import chunk from 'lodash/chunk'
import flatMap from 'lodash/flatMap'
import get from 'lodash/get'
import isUndefined from 'lodash/isUndefined'
import map from 'lodash/map'
import omitBy from 'lodash/omitBy'
import size from 'lodash/size'
import toNumber from 'lodash/toNumber'
import matchPath from '../../routes/match-path'
import { createAuthorization } from '../auth'
import { getLocationQuery, getLocationPathname } from '../location'
import { SORT_TYPE_SEASON_EPISODE } from './sort-types'
import { CONTENT_TYPE_SERIES, CONTENT_TYPE_VIDEO } from './content-types'
import { PRODUCT_TYPE_EPISODE, PRODUCT_TYPE_SEGMENT, PRODUCT_TYPE_SINGLE } from './product-types'

/**
 * The content store key
 */
export const CONTENT_STORE_KEY = 'content'

/**
 * The content path generation store key
 */
export const CONTENT_PATH_STORE_KEY = 'contentPath'

/**
 * The application key for content create
 */
export const CONTENT_CREATE_APPLICATION_KEY = 'create'

/**
 * The application key for content detail
 */
export const CONTENT_DETAIL_APPLICATION_KEY = 'detail'

/**
 * The application key for bulk content application data
 */
export const CONTENT_BULK_APPLICATION_KEY = 'bulk'

export const CONTENT_BULK_STATE_UNPROCESSED = 'unprocessed'
export const CONTENT_BULK_STATE_PROCESSING = 'processing'
export const CONTENT_BULK_STATE_PROCESSED = 'processed'

/**
 * The fields on content for media
 */
const CONTENT_MEDIA_FIELDS = Set(['previewMediaId', 'featureMediaId'])

/**
 * The fields allowed for bulk content import
 */
export const CONTENT_BULK_FIELDS = Set([
  'contentId',
  'contentType',
  'language',
  'primaryLanguage',
  'title',
  'shortDescription',
  'description',
  'seoTitle',
  'seoDescription',
  'seoRobotsNoFollow',
  'seoRobotsNoIndex',
  'path',
  'published',
  'publishedAtDate',
  'previewMediaId',
  'featureMediaId',
  'episodeNumber',
  'seasonNumber',
  'displayType',
  'productType',
  'productionStatus',
  'instructor',
  'director',
  'producer',
  'writer',
  'cast',
  'copyright',
  'studio',
  'contentSearchNoIndex',
  'familyFriendly',
  'seoCanonicalUrl',
  'seoKeywords',
  'bundle',
  'filterType',
  'weight',
  'descriptionFormat',
  'shortDescriptionFormat',
  'contentPolicyPolicyId',
  'dayNumber',
  'directionsLabel',
  'directionsDescription',
  'assetsLabel',
  'assetsDescription',
  'excludeFromRecommendations',
  'peopleListId',
  'thirdPartyDescription',
  'redirect',
])

/**
 * The bulk import fields that are integers
 */
export const CONTENT_BULK_INTEGER_FIELDS = Set([
  'contentPolicyPolicyId',
  'previewMediaId',
  'featureMediaId',
  'episodeNumber',
  'seasonNumber',
  'contentId',
  'weight',
])

export const ID_REGEX = /^[0-9]+/

export const CONTENT_ROOTS = {
  BASE: '/content',
  V2: '/v2/content',
}

export const CONTENT_PAGE_TYPES = {
  LIST: 'list',
  LIST_V2: 'list-v2',
  LIST_YI: 'list-YI',
  REDIRECTS: 'redirects',
  SEARCH: 'search',
  CREATE: 'create',
  BULK: 'bulk',
  DETAIL: 'detail',
  CREATE_V2: 'create-v2',
}

/**
 * Get a single piece of content by contentId and contentType
 * @param {Object} options The options
 * @param {String} options.server The server url
 * @param {Map} options.auth An immutable map with the auth data
 * @param {Number} options.contentId The contentId of the content
 * @param {Number} options.contentType The contentType of the content
 * @param {String} options.language The language to filter by
 */
export async function getContent (options = {}) {
  const { auth, server, contentId, contentType, language } = options
  const content = await getContentList({
    content: [{ contentId, contentType }],
    language,
    server,
    auth,
  })
  return content.getIn(['content', 0])
}

/**
 * Get a list of content from brooklyn by contentIds and contentTypes
 * @param {Object} options The options
 * @param {{contentId:Number, contentType:String}[]} options.content A list of content
 *   with contentId and contentType
 */
export async function getContentList (options = {}) {
  const {
    auth,
    server,
    bundle,
    id,
    include,
    language,
    offset,
    limit,
    title,
    mediaId,
    seasonNumber,
    published,
    publishedAtDate,
    content,
    contentChunkSize = 10,
    contentId,
    contentType,
    filterType,
    referencesTo,
    sort,
    sortDirection,
    primaryLanguageFallback,
  } = options
  const params = omitBy(
    {
      bundle,
      id,
      include,
      language,
      offset,
      limit,
      title,
      mediaId,
      seasonNumber,
      published,
      publishedAtDate,
      content,
      contentId,
      contentType,
      filterType,
      referencesTo,
      sort,
      sortDirection,
      primaryLanguageFallback,
    },
    isUndefined,
  )
  const chunkedContent = chunk(content, contentChunkSize)
  const chunkedParams =
    size(chunkedContent) > 1 ? map(chunkedContent, (c) => ({ ...params, content: c })) : [params]
  const chunkedRes = await mapPromise(chunkedParams, async (paramsToSend) => {
    const res = await request
      .get(`${server}/v1/content/list`)
      .set('accept', 'application/json')
      .set('authorization', createAuthorization(auth))
      .query(qsStringifyQuery(paramsToSend, { arrayFormat: 'indices', encodeValuesOnly: true }))
    return get(res, 'body', {})
  })
  const chunkedResContent = flatMap(map(chunkedRes, 'content'))
  return fromJS({
    currentPage: size(chunkedRes) === 1 ? get(chunkedRes, '[0].currentPage') : 0,
    totalCount:
      size(chunkedRes) === 1 ? get(chunkedRes, '[0].totalCount') : size(chunkedResContent),
    content: chunkedResContent,
  })
}

/**
 * Create content
 * @param {Object} options The options
 * @param {Map} options.auth The auth state
 * @param {String} options.server The server
 * @param {Map} options.data The content data
 * @param {Boolean} [options.generatePath=false] When true the update endpoint
 *   will generate an updated path
 */
export async function createContent (options = {}) {
  const { auth, server, data, generatePath, createTranslationJob } = options
  if (!Map.isMap(data)) {
    throw new Error('The data option is required to be a Map')
  }
  const res = await request
    .post(`${server}/v1/content`)
    .query({ generatePath, createTranslationJob })
    .send(data.toJS())
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', {}))
}

/**
 * Update content
 * @param {Object} options The options
 * @param {Map} options.auth The auth state
 * @param {String} options.server The server
 * @param {Map} options.data The content data
 * @param {Boolean} [options.generatePath=false] When true the update endpoint
 *   will generate an updated path
 */
export async function updateContent (options = {}) {
  const { auth, server, data, generatePath } = options
  if (!Map.isMap(data)) {
    throw new Error('The data option is required to be a Map')
  }
  if (!data.get('id')) {
    throw new Error('The data option is required to have an id property')
  }
  const res = await request
    .put(`${server}/v1/content/${data.get('id')}`)
    .query({ generatePath })
    .send(data.toJS())
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', {}))
}

/**
 * Remove content
 * @param {Object} options The options
 * @param {Map} options.auth The auth state
 * @param {String} options.server The server
 * @param {String} options.contentId The content data
 */
export async function removeContent (options = {}) {
  const { auth, server, contentId } = options
  if (!contentId) {
    throw new Error('The contentId option is required')
  }
  const res = await request
    .delete(`${server}/v1/content/${contentId}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', {}))
}

/**
 * Update content list
 * @param {Object} options The options
 * @param {Map} options.auth The auth state
 * @param {String} options.server The server
 * @param {List} options.content List of content data
 * @param {Boolean} [options.generatePath=false] When true
 *   the update endpoint will generate an updated path
 * @param {Boolean} [options.redirectOnUnpublish=false] When true the update endpoint
 *   will create a redirect if the content is being unpublished (changing to published=false)
 */
export async function updateContentList (options = {}) {
  const { auth, server, content, generatePath, redirectOnUnpublish, pushToContentReferences } =
    options
  if (!List.isList(content)) {
    throw new Error('The content option is required to be a List')
  }
  const res = await request
    .post(`${server}/v1/content/list`)
    .query({
      generatePath,
      redirectOnUnpublish,
      pushToContentReferences,
    })
    .send({ content: content.toJS() })
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', {}))
}

export const uploadBulkTranslation = async (options = {}) => {
  const { auth, server, body } = options

  await request
    .post(`${server}/v1/content/bulk-translate`)
    .send(body)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
}

/**
 * Generates a path based on the Contents' type and title
 * @param {Object} options The options
 * @param {String} options.server The server url
 * @param {Map} options.auth An immutable map with the auth data
 * @param {Map} options.data An immutable map with the data to send
 * @param {String} options.data.title The title of the content
 * @param {Number} options.data.contentId The contentId of the content
 * @param {String} options.data.contentType The contentType of the content
 */
export async function getContentPathFromContentTitle (options = {}) {
  const { auth, server, data } = options
  if (!Map.isMap(data)) {
    throw new Error('The data option is required to be a Map')
  }
  const res = await request
    .post(`${server}/v1/content/path`)
    .send(data.toJS())
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', {}))
}

export const fetchExistingTranslation = async ({ auth, server, content }) => {
  if (!content) {
    throw new Error('The content option is required')
  }

  const contentId = content.get('contentId')
  const contentType = content.get('contentType')
  const language = content.get('language')

  await request
    .get(`${server}/v1/download-translation`)
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))
    .query({ contentId, contentType, language })
}

/**
 * Helper function to derive which page is required based on the location url
 *
 * @param {*} location the location state
 * @returns which content page type is requested from the location url
 */
export function getContentPageType (location) {
  const pathname = getLocationPathname(location)
  if (pathname.includes(CONTENT_ROOTS.V2)) {
    return CONTENT_PAGE_TYPES.LIST_V2
  }

  const id = getIdFromLocation(location)
  if (ID_REGEX.test(id)) {
    return CONTENT_PAGE_TYPES.DETAIL
  }
  switch (id) {
    case CONTENT_PAGE_TYPES.REDIRECTS:
      return CONTENT_PAGE_TYPES.REDIRECTS
    case CONTENT_PAGE_TYPES.SEARCH:
      return CONTENT_PAGE_TYPES.SEARCH
    case CONTENT_PAGE_TYPES.CREATE:
      return CONTENT_PAGE_TYPES.CREATE
    case CONTENT_PAGE_TYPES.BULK:
      return CONTENT_PAGE_TYPES.BULK
    default:
      return CONTENT_PAGE_TYPES.LIST
  }
}

/**
 * Given the location state get the content id
 * @param {Map} location The location state
 * @returns {String|undefined} The content id if found in the location
 */
export function getIdFromLocation (location = Map()) {
  if (!Map.isMap(location)) {
    throw new Error('The location argument is required to be a Map')
  }
  const match = matchPath({
    pathname: getLocationPathname(location),
    path: '/content/:id',
  })

  const id = get(match, 'params.id')
  if (!id) {
    return id
  }
  return ID_REGEX.test(id) ? toNumber(id) : id
}

/**
 * Given a piece of content return a key of the form: contentId-contentType-language
 * @param {Map} content The location state
 * @returns {String|undefined} The unique key for the given piece of content
 */
export function getContentKey (content = Map()) {
  if (content.isEmpty()) {
    return undefined
  }
  return `${content.get('contentId')}-${content.get('contentType')}-${content.get('language', 'en')}`
}

/**
 * Return true when in content detail page
 * @param {Object} state Redux state
 * @param {Map} state.location The location state
 * @return {Boolean} True when the pathname starts '/content' and is followed with an id
 */
export function isContentDetailPage (state = {}) {
  const { location } = state
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  const id = getIdFromLocation(location)
  return ID_REGEX.test(id)
}

/**
 * Return true when in content detail page
 * @param {Object} state Redux state
 * @param {Map} state.location The location state
 * @return {Boolean} True when the pathname is '/content/create'
 */
export function isContentCreatePage (state = {}) {
  const { location } = state
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  return getLocationPathname(location) === `${CONTENT_ROOTS.BASE}/${CONTENT_PAGE_TYPES.CREATE}`
}

/**
 * Return true when in content bulk page
 * @param {Object} state Redux state
 * @param {Map} state.location The location state
 * @return {Boolean} True when the pathname is '/content/bulk'
 */
export function isContentBulkPage (state = {}) {
  const { location } = state
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  return getLocationPathname(location) === `${CONTENT_ROOTS.BASE}/${CONTENT_PAGE_TYPES.BULK}`
}

/**
 * Return true when in content list page
 * @param {Object} state Redux state
 * @param {Map} state.location The location state
 * @return {Boolean} True when the pathname is '/content'
 */
export function isContentListPage (state = {}) {
  const { location } = state
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  return getLocationPathname(location) === `${CONTENT_ROOTS.BASE}`
}

export function locationToOptions (location = Map()) {
  return location.getIn(['data', 'query'], Map())
}

/**
 * Given state create the content list options
 * @param {Object} state The parameters
 * @param {Map} state.location The location state
 */
export function createContentListViewOptions (state = {}) {
  const { location } = state
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  const query = getLocationQuery(location)
  return Map({
    offset: query.get('offset') || 0,
    limit: query.get('limit') || 50,
    title: query.get('title'),
    published: query.get('published'),
    id: query.get('id'),
    language: query.get('language'),
    contentId: query.get('contentId'),
    mediaId: query.get('mediaId'),
    contentType: query.get('contentType'),
    sort: query.get('sort'),
    sortDirection: query.get('sortDirection') || 'desc',
    // If language is not provided fallback to the primary lanaguage so we only get
    // 1 content item per row
    primaryLanguageFallback: query.get('primaryLanguageFallback') || !query.get('language'),
    publishedAtDate: query.get('publishedAtDate'),
  })
}

/**
 * Given state create the content list options
 * @param {Object} state The parameters
 * @param {Map} state.location The location state
 */
export function createContentDetailViewOptions (state = {}) {
  const { location } = state
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  const options = Map({
    detail: true,
    generatePath: true,
    id: getIdFromLocation(location),
    include: isContentDetailPage(state)
      ? Set([
          'media',
          'images',
          'content_references',
          'episodes',
          'content_translations',
          'season_numbers',
          'content_list_criteria',
        ])
      : Set(),
  })
  return options
}

/**
 * Given state create the content list options
 * @param {Object} options The options
 * @param {Map} options.content The location state
 */
export function createContentMediaOptions (options = {}) {
  const { content } = options
  if (!Map.isMap(content)) {
    throw new Error('The content property is required to be a Map on the options parameter')
  }
  return Map({
    mediaId: CONTENT_MEDIA_FIELDS.map((field) => {
      const val = content.get(field)
      return val && typeof val === 'object' ? val.mediaId : val
    }).filter((v) => v),
    include: Set(['media_info', 'media_assets', 'content_policy']),
  })
}

export function createContentReferencesToOptions (options = {}) {
  const { content, primaryLanguageFallback = false, sort, type = null, seasonNumber } = options
  const contentList = Map.isMap(content) ? List([content]) : content
  return Map({
    seasonNumber: seasonNumber || undefined,
    referencesTo: contentList.map((v) =>
      Map({
        contentId: v.get('contentId'),
        contentType: v.get('contentType'),
        type,
      }),
    ),
    /**
     * @todo update to array when supported
     */
    language: contentList.map((v) => v.get('language')).first(),
    sort,
    primaryLanguageFallback,
  })
}

export function createContentEpisodesOptions (options = {}) {
  const { content } = options
  const seasonNumber = content.size === 1 ? content.first().get('seasonNumber') : undefined
  return createContentReferencesToOptions({
    seasonNumber,
    content,
    sort: SORT_TYPE_SEASON_EPISODE,
    type: CONTENT_TYPE_SERIES,
  })
}

/**
 * Create a view for images content relations
 * @param {Map} content The content state
 */
export function createContentImagesRelationshipViewOptions (content) {
  return Map({
    contentId: content.get('contentId'),
    contentType: content.get('contentType'),
    language: content.get('language'),
  })
}

/**
 * Create a view for a content for transationlation
 * @param {List|Map} content A list of content maps with contentId and contentType or a conte
 */
export function createContentTranslationsViewOptions (content) {
  const contentList = Map.isMap(content) ? List([content]) : content
  return Map({
    primaryLanguageFallback: false,
    content: contentList
      .map((v) =>
        Map({
          contentId: v.get('contentId'),
          contentType: v.get('contentType'),
        }),
      )
      .toSet()
      .toList(),
  })
}

/**
 * Given state create the content create options
 * @param {Object} state Redux state
 * @param {Map} state.location The location state
 */
export function createContentCreateViewOptions (state = {}, options = {}) {
  const { location } = state
  const { createTranslationJob } = options
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  const query = getLocationQuery(location)
  return Map({
    create: true,
    createTranslationJob,
    generatePath: true,
    translation: Boolean(query.get('contentId') && query.get('contentType')),
    contentId: query.get('contentId'),
    contentType: query.get('contentType'),
    primaryLanguageFallback: false,
  })
}

/**
 * Given state create the content path create path options
 * @param {Object} state Redux state
 * @param {Map} state.content The content state
 */
export function createContentPathCreateViewOptions (state = {}) {
  const { content } = state
  if (!Map.isMap(content)) {
    throw new Error('The content property is required to be a Map on the state parameter')
  }
  return Map({
    title: content.get('title'),
    contentId: content.get('contentId'),
    contentType: content.get('contentType'),
  })
}

/**
 * Create the initial data for a translated content record
 * using the primary language content for the defaults
 * @param {Map} content The primary language content
 */
export function createContentTranslation (content) {
  const fields = Set([
    'assetsDescription',
    'assetsLabel',
    'bundle',
    'cast',
    'contentId',
    'contentPolicyPolicyId',
    'contentSearchNoIndex',
    'contentType',
    'copyright',
    'dayNumber',
    'description',
    'descriptionFormat',
    'directionsDescription',
    'directionsLabel',
    'director',
    'displayType',
    'episodeNumber',
    'excludeFromRecommendations',
    'familyFriendly',
    'featureMediaId',
    'filterType',
    'instructor',
    'path',
    'peopleListId',
    'previewMediaId',
    'primaryLanguage',
    'producer',
    'productType',
    'productionStatus',
    'publishedAtDate',
    'seasonNumber',
    'seoCanonicalUrl',
    'seoDescription',
    'seoKeywords',
    'seoRobotsNoFollow',
    'seoRobotsNoIndex',
    'seoTitle',
    'shortDescription',
    'shortDescriptionFormat',
    'studio',
    'thirdPartyDescription',
    'title',
    'weight',
    'writer',
  ])
  return content.filter((_, k) => fields.has(k)).set('published', false)
}

/**
 * A list of supported content filtered display
 */
const FILTERED_DISPLAY_OPTIONS = OrderedSet([
  Map({ label: 'Display series', value: [CONTENT_TYPE_SERIES] }),
  Map({ label: 'Display episodes', value: [CONTENT_TYPE_VIDEO, PRODUCT_TYPE_EPISODE] }),
  Map({ label: 'Display singles', value: [CONTENT_TYPE_VIDEO, PRODUCT_TYPE_SINGLE] }),
  Map({ label: 'Display segments', value: [CONTENT_TYPE_VIDEO, PRODUCT_TYPE_SEGMENT] }),
])

export function getFilteredDisplayOptions () {
  return FILTERED_DISPLAY_OPTIONS
}
